import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import * as Icons from "lucide-react";
import { ICONS_BY_NAME } from "../../lib/icons";
import AuthImg from "../../components/AuthImg";
import AuthFileLink from "../../components/AuthFileLink";

const NAV = [
  { id: "logos", label: "Logo", icon: "Shapes" },
  { id: "couleurs", label: "Couleurs", icon: "Palette" },
  { id: "typographie", label: "Typographie", icon: "Type" },
];

const isImg = (name = "") => /\.(png|jpe?g|webp|svg)$/i.test(name);

export default function ClientIdentity() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [active, setActive] = useState("logos");
  useEffect(() => { api.get("/client/identity").then((r) => setD(r.data)); }, []);
  if (!d) return null;

  const empty = !d.palette && !d.logos && !d.typography;
  if (empty) return <div className="p-2"><h1 className="font-display text-4xl text-marine">Charte de marque</h1><p className="text-sm text-marine/80 mt-4">Votre charte n'est pas encore disponible. Elle sera publiée à la fin de votre projet Identité Visuelle.</p></div>;

  const kit = (d.files || []).find((f) => f.kind === "kit");
  const charte = (d.files || []).find((f) => f.kind === "charte");
  const logos = d.logos || [];
  const palette = d.palette || [];
  const typography = d.typography || [];

  const scrollTo = (id) => { setActive(id); document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <div>
      {/* Hero */}
      <div className="bg-marine rounded-sm p-10 lg:p-14 mb-10 relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-bordeaux/40 blur-3xl" />
        <div className="relative z-10">
          <div className="text-xs uppercase tracking-[0.3em] text-white/60 mb-3">Charte de marque</div>
          <h1 className="font-display text-4xl lg:text-5xl text-white leading-tight">{user?.name}</h1>
          <p className="text-white/70 mt-3 max-w-lg">Guide d'utilisation de votre identité visuelle — logo, couleurs et typographies. Créé et maintenu par Metara.</p>
          {(kit || charte) && (
            <div className="flex gap-3 mt-6">
              {kit && <AuthFileLink path={kit.path} data-testid="download-kit" className="bg-white text-marine rounded-sm px-5 py-2.5 text-sm font-medium hover:bg-white/90 flex items-center gap-2"><Icons.Download size={16} /> Kit complet</AuthFileLink>}
              {charte && <AuthFileLink path={charte.path} data-testid="download-charte" className="border border-white/40 text-white rounded-sm px-5 py-2.5 text-sm hover:bg-white/10 flex items-center gap-2"><Icons.FileText size={16} /> Charte PDF</AuthFileLink>}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-10">
        {/* Left section nav */}
        <aside className="hidden lg:block w-48 shrink-0">
          <div className="sticky top-24 space-y-1">
            {NAV.map((n) => {
              const Ic = ICONS_BY_NAME[n.icon];
              return (
                <button key={n.id} onClick={() => scrollTo(n.id)} data-testid={`brand-nav-${n.id}`}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${active === n.id ? "bg-marine text-white" : "text-marine hover:bg-white"}`}>
                  <Ic size={16} /> {n.label}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-16">
          {/* LOGO */}
          <section id="logos">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-bordeaux mb-2">Logo</div>
            <h2 className="font-display text-3xl text-marine mb-6">Le logo</h2>
            {logos.length === 0 ? <p className="text-sm text-marine/80">Aucun logo disponible pour le moment.</p> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {logos.map((l, i) => (
                  <div key={i} className="rounded-sm border border-marine/10 overflow-hidden bg-sable">
                    <div className={`h-40 flex items-center justify-center p-6 ${i % 2 ? "bg-marine" : "beige-texture"}`}>
                      {l.path && isImg(l.name || l.path) ? <AuthImg path={l.path} alt={l.label} className="max-h-full max-w-full object-contain" />
                        : <Icons.Image size={16} className="text-marine/30" />}
                    </div>
                    <div className="p-3 flex items-center justify-between">
                      <span className="text-sm text-marine">{l.label || l.name}</span>
                      {l.path && <AuthFileLink path={l.path} data-testid={`logo-dl-${i}`} className="text-marine hover:text-bordeaux inline-flex"><Icons.Download size={16} /></AuthFileLink>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* COULEURS */}
          <section id="couleurs">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-bordeaux mb-2">Couleurs</div>
            <h2 className="font-display text-3xl text-marine mb-6">La palette</h2>
            {palette.length === 0 ? <p className="text-sm text-marine/80">Aucune couleur définie.</p> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {palette.map((c, i) => (
                  <div key={i} className="rounded-sm border border-marine/10 overflow-hidden bg-sable group">
                    <div className="h-32 relative" style={{ background: c.hex }}>
                      <button onClick={() => navigator.clipboard?.writeText(c.hex)} title="Copier le HEX"
                        className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 rounded-sm p-1"><Icons.Copy size={16} className="text-marine" /></button>
                    </div>
                    <div className="p-3">
                      <div className="text-sm font-medium text-marine">{c.name}</div>
                      <div className="text-xs text-marine/80 font-mono uppercase">{c.hex}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* TYPOGRAPHIE */}
          <section id="typographie">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-bordeaux mb-2">Typographie</div>
            <h2 className="font-display text-3xl text-marine mb-6">Les typographies</h2>
            {typography.length === 0 ? <p className="text-sm text-marine/80">Aucune typographie définie.</p> : (
              <div className="space-y-4">
                {typography.map((t, i) => {
                  const serif = /playfair|serif|georgia|times/i.test(t.name);
                  return (
                    <div key={i} className="bg-sable rounded-sm border border-marine/10 p-6">
                      <div className="flex items-baseline justify-between mb-4">
                        <div className="text-xs text-bordeaux uppercase tracking-wider">{t.usage || "Usage"}</div>
                        <div className="text-sm text-marine/80">{t.name}</div>
                      </div>
                      <div className={`text-4xl text-marine mb-2 ${serif ? "font-display" : "font-sans"}`}>{t.name}</div>
                      <div className={`text-lg text-gris-texte ${serif ? "font-display" : "font-sans"}`}>Aa Bb Cc Dd · 0 1 2 3 4 5 6 7 8 9</div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* RÈGLES (optionnel) */}
          {d.rules && (d.rules.dos?.length || d.rules.donts?.length) ? (
            <section id="regles">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-bordeaux mb-2">Bonnes pratiques</div>
              <h2 className="font-display text-3xl text-marine mb-6">Règles d'usage</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-sable rounded-sm border border-bordeaux/20 p-5">
                  <div className="text-sm font-semibold text-bordeaux mb-2 flex items-center gap-2"><Icons.Check size={16} /> À faire</div>
                  <ul className="space-y-1 text-sm text-marine">{(d.rules.dos || []).map((r, i) => <li key={i}>• {r}</li>)}</ul>
                </div>
                <div className="bg-sable rounded-sm border border-rouge/20 p-5">
                  <div className="text-sm font-semibold text-rouge-texte mb-2 flex items-center gap-2"><Icons.X size={16} /> À éviter</div>
                  <ul className="space-y-1 text-sm text-marine">{(d.rules.donts || []).map((r, i) => <li key={i}>• {r}</li>)}</ul>
                </div>
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
